import type { UserContext, UserContextAccountType } from 'core/userContext';
import { USER_ACCOUNT_TYPES } from 'core/userContext/constants';
import { DATA_SCHEMA_MANAGEMENT_URL } from 'modules/dataDictionary/constants';
import {
  ORG_PERMISSIONS,
  PROJECT_PERMISSIONS,
  doesRbacPermissionHaveAccess,
} from 'utils/permissions';
import { NavbarOption, NavbarProjectSettings } from '../types';
import { hasFeatureFlagEnabled } from './hasFeatureFlagEnabled';

type FilterLinksByAccessibleParams = {
  links: NavbarOption[];
  accountType?: UserContextAccountType;
  featureFlags: UserContext['featureFlags'];
  rbacPermissions?: number[];
  settings?: NavbarProjectSettings;
};

/** Filter list of NavbarOption links based on account type, permissions, feature flags and ad-hoc settings */
export const filterLinksByAccessible = ({
  links,
  accountType,
  featureFlags = {},
  rbacPermissions = [],
  settings = {} as Record<string, boolean>,
}: FilterLinksByAccessibleParams) =>
  links
    .map((link) => {
      const {
        rbacPermission = [],
        featureFlags: linkFeatureFlags = [],
        hideFromProspect,
        projectSettings = [],
        links: nestedLinks,
      } = link;

      // check if user is a prospect
      const isProspect = accountType === USER_ACCOUNT_TYPES.Prospect;
      if (isProspect && hideFromProspect) return null;

      // check permissions and flags
      const hasPermission = doesRbacPermissionHaveAccess(rbacPermissions, rbacPermission);
      const hasFlagEnabled = hasFeatureFlagEnabled(featureFlags, linkFeatureFlags);

      // check for additional project setting requirements
      const hasProjectSettingsEnabled = projectSettings.every((setting) => !!settings[setting]);

      // If the top-level link is inaccessible, skip it
      if (!(hasPermission && hasFlagEnabled && hasProjectSettingsEnabled)) return null;

      // Filter nested links (if present)
      const filteredNestedLinks = nestedLinks?.filter((nestedLink) => {
        const { featureFlags: nestedFeatureFlags = [], requireOnlyOneFlag = false } = nestedLink;
        return hasFeatureFlagEnabled(featureFlags, nestedFeatureFlags, requireOnlyOneFlag);
      });

      return {
        ...link,
        ...(filteredNestedLinks ? { links: filteredNestedLinks } : {}),
      };
    })
    .filter((link) => link !== null); // Remove null values from the array

export const createCurrentProjectLinks = ({
  accountType,
  featureFlags,
  projectId,
  rbacPermissions,
}: {
  accountType?: UserContextAccountType;
  featureFlags: UserContext['featureFlags'];
  projectId?: number;
  rbacPermissions?: number[];
}) =>
  filterLinksByAccessible({
    accountType,
    featureFlags,
    rbacPermissions,
    links: [
      {
        title: 'Project Settings',
        href: '/settings/project',
        rbacPermission: PROJECT_PERMISSIONS.ManageProjectSettings,
        // NOTE: the project settings route isn't actual an "external" link, but
        // we need to do a hard refresh for this route because the route requires
        // something to function that only occurs when the backend route is loaded.
        // once that functionality is fixed, this can be removed
        isExternal: true,
      },
      {
        title: 'Alerts',
        href: '/settings/alerts/campaigns',
        rbacPermission: PROJECT_PERMISSIONS.ManageProjectSettings,
        featureFlags: ['CampaignPerformanceAlerts'],
      },
      {
        title: 'Custom Event Usage',
        href: '/settings/customEvents',
        rbacPermission: PROJECT_PERMISSIONS.ManageProjectSettings,
        featureFlags: [
          {
            name: 'SchemaManagementUI',
            disabled: true,
          },
        ],
      },
      {
        title: 'Data Schema Management',
        href: DATA_SCHEMA_MANAGEMENT_URL,
        rbacPermission: PROJECT_PERMISSIONS.ManageProjectSettings,
        featureFlags: ['SchemaManagementUI'],
      },
      {
        title: 'Project Members',
        href: `/projects/${projectId?.toString()}`, // Path is, projectId must be added dynamically '/projects/:projectId'
        rbacPermission: ORG_PERMISSIONS.ManageMembers,
      },
      {
        title: 'Message Channels and Types',
        href: '/settings/channels/email',
        rbacPermission: PROJECT_PERMISSIONS.ViewChannelConfiguration,
      },
      {
        title: 'Frequency Management',
        href: '/settings/frequencyManagement/email',
        featureFlags: ['FrequencyCappingMessageType'],
      },
      {
        title: 'Senders',
        href: '/settings/senders',
        divider: true,
        rbacPermission: PROJECT_PERMISSIONS.ViewChannelConfiguration,
        // NOTE: the project senders route isn't actual an "external" link, but
        // we need to do a hard refresh for this route because the route requires
        // something to function that only occurs when the backend route is loaded.
        // once that functionality is fixed, this can be removed
        isExternal: true,
      },
      {
        title: 'DNS Setup',
        href: '/settings/dnsSetup',
        rbacPermission: PROJECT_PERMISSIONS.ViewChannelConfiguration,
      },
      {
        title: 'Sending Platforms',
        href: '/settings/sendingPlatforms',
        rbacPermission: PROJECT_PERMISSIONS.ViewChannelConfiguration,
      },
      {
        title: 'Domains',
        href: '/settings/domains',
        rbacPermission: PROJECT_PERMISSIONS.ViewChannelConfiguration,
        featureFlags: ['SMSLinkShortening'],
      },
      {
        title: 'Apps and Websites',
        href: '/integrations/mobileApps',
        divider: true,
        rbacPermission: PROJECT_PERMISSIONS.ViewChannelConfiguration,
      },
      {
        title: 'Web Push',
        href: '/integrations/webPush',
        divider: true,
        rbacPermission: PROJECT_PERMISSIONS.ViewChannelConfiguration,
      },
      {
        title: 'Embedded Placements',
        href: '/settings/placements',
        divider: true,
        rbacPermission: PROJECT_PERMISSIONS.ViewChannelConfiguration,
        featureFlags: [
          {
            name: 'EmbeddedMessagingCore',
            disabled: false,
          },
        ],
      },
    ],
  });
